import { useCallback, useEffect, useRef, useState } from 'react';
import { useReducedMotion } from './useReducedMotion';

/**
 * Determine if the browser supports hardware acceleration for animations
 * Used to make smart decisions about animation complexity
 */
export const useHardwareAcceleration = () => {
    const [isHardwareAccelerated, setIsHardwareAccelerated] = useState(true);

    useEffect(() => {
        // Check for low-end device indicators
        const isLowEndDevice = () => {
            // Check memory (if available)
            if ('deviceMemory' in navigator) {
                const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
                if (memory && memory < 4) {
                    return true;
                }
            }

            // Check for mobile device
            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
                navigator.userAgent
            );

            // Check for slow CPU cores (if available)
            if ('hardwareConcurrency' in navigator) {
                const cores = navigator.hardwareConcurrency;
                if (cores && cores < 4 && isMobile) {
                    return true;
                }
            }

            return false;
        };

        setIsHardwareAccelerated(!isLowEndDevice());
    }, []);

    return isHardwareAccelerated;
};

/**
 * Optimize animations using requestAnimationFrame
 * This is much more efficient than using setTimeout/setInterval
 */
export const useAnimationFrame = (
    callback: (deltaTime: number) => void,
    dependencies: React.DependencyList = []
) => {
    const requestRef = useRef<number | null>(null);
    const previousTimeRef = useRef<number | null>(null);

    const animate = useCallback((time: number) => {
        if (previousTimeRef.current !== null) {
            const deltaTime = time - previousTimeRef.current;
            callback(deltaTime);
        }
        previousTimeRef.current = time;
        requestRef.current = requestAnimationFrame(animate);
    }, [callback]);

    useEffect(() => {
        requestRef.current = requestAnimationFrame(animate);
        return () => {
            if (requestRef.current !== null) {
                cancelAnimationFrame(requestRef.current);
            }
        };
    }, [animate, ...dependencies]);
};

/**
 * Throttle animations based on device capability and user preferences
 * Respects reduced motion and hardware capability
 */
export const useThrottledAnimation = (
    fps = 60,
    respectReducedMotion = true
) => {
    const prefersReducedMotion = useReducedMotion();
    const isHardwareAccelerated = useHardwareAcceleration();

    // Adjust FPS based on hardware capability and user preferences
    const effectiveFps = (() => {
        if (respectReducedMotion && prefersReducedMotion) {
            return Math.min(fps, 30); // Cap at 30fps for reduced motion
        }

        if (!isHardwareAccelerated) {
            return Math.min(fps, 30); // Cap at 30fps for low-end devices
        }

        return fps;
    })();

    // Calculate frame duration in ms
    const frameDuration = 1000 / effectiveFps;

    // Create a throttle function for animations
    const throttleAnimation = useCallback((callback: FrameRequestCallback) => {
        let lastFrameTime = 0;

        return (time: number) => {
            if (time - lastFrameTime >= frameDuration) {
                lastFrameTime = time;
                callback(time);
            }
        };
    }, [frameDuration]);

    return {
        throttleAnimation,
        effectiveFps,
        isHardwareAccelerated,
        prefersReducedMotion,
    };
};

/**
 * Use GPU-accelerated properties for animations
 * Returns transformed CSS properties that use the GPU where possible
 */
export const useGPUAcceleratedProps = (
    transform = '',
    useGpu = true
): { transform: string; willChange: string } => {
    const prefersReducedMotion = useReducedMotion();
    const isHardwareAccelerated = useHardwareAcceleration();

    // Only use GPU acceleration if appropriate
    const shouldUseGpu = useGpu && isHardwareAccelerated && !prefersReducedMotion;

    return {
        // Add transform properties that trigger GPU acceleration
        transform: shouldUseGpu
            ? `${transform} translateZ(0)`
            : transform,
        // Set will-change for browsers to optimize ahead of time
        willChange: shouldUseGpu ? 'transform, opacity' : 'auto',
    };
};

export default {
    useHardwareAcceleration,
    useAnimationFrame,
    useThrottledAnimation,
    useGPUAcceleratedProps,
}; 